import type { ReactNode } from 'react';
import { Dialog, DialogContent, DialogContentText, DialogTitle, MenuItem } from '@mui/material';
import { CustomField } from 'src/components/molecules/formik/CustomField';
import { useGetWarehouses } from 'src/hooks/api/requestConfigs/warehouse';
import { useSettingsContext } from 'src/hooks/useSettings';

// Reuse the existing persisted setting, including the webclient settings form.
export const ACTIVE_WAREHOUSE_SETTING = 'dispatchOrdersPrinterWarehouseId';
export type WarehouseId = string | number;
const hasWarehouseId = (value: unknown): value is WarehouseId => (typeof value === 'string' && value.trim() !== '') || (typeof value === 'number' && Number.isFinite(value));

export const useActiveWarehouse = () => {
    const { settings, setSetting } = useSettingsContext();
    const value = settings?.[ACTIVE_WAREHOUSE_SETTING];
    const warehouseId: WarehouseId | null = hasWarehouseId(value) ? value : null;
    const selectWarehouse = (id: WarehouseId) => { if (hasWarehouseId(id)) setSetting(ACTIVE_WAREHOUSE_SETTING, id); };
    return { warehouseId, selectWarehouse };
};

type WarehouseSelectorProps = { value: WarehouseId | null; onChange: (value: WarehouseId) => void; disabled?: boolean; label?: string };

// Controlled so pages can retain their save/discard guards and settings can use Formik.
const WarehouseSelector = ({ value, onChange, disabled = false, label = 'Warehouse' }: WarehouseSelectorProps) => {
    const warehouses = useGetWarehouses();
    return (
        <CustomField options={{ name: label, select: true, disabled, height: '40px', topMargin: '0px' }} stateProps={{ state: value ?? '', setState: onChange }}>
            {warehouses?.map((warehouse) => <MenuItem key={warehouse.WarehouseId} value={warehouse.WarehouseId}>{warehouse.WarehouseName}</MenuItem>)}
        </CustomField>
    );
};

export const WarehouseSelectionPopup = () => {
    const { warehouseId, selectWarehouse } = useActiveWarehouse();
    if (warehouseId != null) return null;
    return (
        <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown aria-labelledby="active-warehouse-title" aria-describedby="active-warehouse-description">
            <DialogTitle id="active-warehouse-title">Select an active warehouse</DialogTitle>
            <DialogContent>
                <DialogContentText id="active-warehouse-description" sx={{ mb: 2 }}>You need to select a warehouse as your active warehouse for this page to load. This selection is shared across all warehouse pages.</DialogContentText>
                <WarehouseSelector value={warehouseId} onChange={selectWarehouse} />
            </DialogContent>
        </Dialog>
    );
};

// Keep warehouse-dependent hooks inside children, so they cannot run before selection.
export const WarehouseSelectionRequired = ({ children }: { children: ReactNode }) => {
    const { warehouseId } = useActiveWarehouse();
    return warehouseId == null ? <WarehouseSelectionPopup /> : <>{children}</>;
};

export default WarehouseSelector;
